import "intro.js/introjs.css";
import "./IntroSystem.css";

import introJs from "intro.js";
import type { Hint } from "intro.js";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import { topbarStore } from "shared/common/stores/topbarStore";
import {
  getIntroductionShownLS,
  setIntroductionShownLS,
  waitForAllElements,
} from "shared/common/utils/general-utils";
import { EditSteps } from "./EditSteps";
import { ViewSteps } from "./ViewSteps";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "../../contexts/SnackbarProvider";
import ClientConfig from "../../../config";

type Announcement = {
  _id: string;
  contentText: string; // dangerouslySetInnerHTML
  anchorElement: string;
  showInStage?: string[];
};

type AnnouncementHint = Announcement & {};

type AnnouncementMedia = {
  path: string;
  altText?: string;
  title?: string;
  type?: string;
  mime?: string;
  _id?: string;
};

type AnnouncementStep = Announcement & {
  title: string;
  contentMedia?: AnnouncementMedia | null; // image/video
  contentMediaHyperlink: string; // hyperlink that gets attached to the contentMedia
  contentYoutubeLink: string;
};

const CMS_BASE_URL = ClientConfig.announcementApiURL;
const CMS_API_KEY = process.env.ANNOUNCEMENT_API_KEY;
const VIDEO_FILE_REGEX = /\.(mp4|webm|ogg)$/i;
const ABSOLUTE_URL_REGEX = /^[a-z][a-z0-9+.-]*:/i;
const INTRO_STORAGE_SUFFIX = "-introShown";

const resolveCmsUrl = (url: string) => {
  if (ABSOLUTE_URL_REGEX.test(url)) return url;
  const normalizedPath = url.startsWith("/") ? url : `/${url}`;
  return `${CMS_BASE_URL}${normalizedPath}`;
};

const resolveMediaUrl = (path: string) => {
  if (ABSOLUTE_URL_REGEX.test(path)) return path;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  const uploadsPath = normalizedPath.startsWith("/storage/uploads/")
    ? normalizedPath
    : `/storage/uploads${normalizedPath}`;
  return `${CMS_BASE_URL}${uploadsPath}`;
};

const isVideoMedia = (media: AnnouncementMedia, mediaPath: string) =>
  media.type === "video" ||
  media.mime?.startsWith("video/") ||
  VIDEO_FILE_REGEX.test(mediaPath);

const toYoutubeEmbedUrl = (url: string) => {
  const shortMatch = url.match(/^https?:\/\/youtu\.be\/([^?&#/]+)(\?[^#]*)?$/i);
  if (shortMatch) {
    return `https://www.youtube.com/embed/${shortMatch[1]}${shortMatch[2] || ""}`;
  }

  const watchMatch = url.match(
    /^https?:\/\/(?:www\.)?youtube\.com\/watch\?(.+)$/i
  );
  if (!watchMatch) return url;

  const params = new URLSearchParams(watchMatch[1]);
  const videoId = params.get("v");
  if (!videoId) return url;
  params.delete("v");

  const query = params.toString();
  return `https://www.youtube.com/embed/${videoId}${query ? `?${query}` : ""}`;
};

const renderMediaMarkup = (step: AnnouncementStep) => {
  const media = step.contentMedia;
  const mediaPath = media?.path?.trim();

  const mediaMarkup =
    media && mediaPath
      ? (() => {
          const mediaSrc = resolveMediaUrl(mediaPath);
          const mediaAlt = media.altText || media.title || "Announcement media";
          const mediaTag = isVideoMedia(media, mediaPath)
            ? `<video controls preload='metadata' src='${mediaSrc}'></video>`
            : `<img loading='lazy' alt='${mediaAlt}' src='${mediaSrc}'/>`;

          return step.contentMediaHyperlink
            ? `<a href='${resolveCmsUrl(step.contentMediaHyperlink)}' target='_blank' rel='noopener noreferrer'>${mediaTag}</a>`
            : mediaTag;
        })()
      : "";

  const youtubeMarkup = step.contentYoutubeLink
    ? `<iframe src='${toYoutubeEmbedUrl(step.contentYoutubeLink)}' title='YouTube video player' frameborder='0' loading='lazy' allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share' referrerpolicy='strict-origin-when-cross-origin' allowfullscreen></iframe>`
    : "";

  return `${mediaMarkup}${youtubeMarkup}`;
};

const getIntroShownStorage = (platform: "edit" | "view" | "api") =>
  JSON.parse(
    localStorage.getItem(`${platform}${INTRO_STORAGE_SUFFIX}`) || "{}"
  );

const getShownIds = (
  platform: "edit" | "view" | "api",
  key: "shownAnnouncementStepIds" | "shownAnnouncementHintIds"
) => {
  const all = getIntroShownStorage(platform);
  return Array.isArray(all[key])
    ? all[key].map((id: string | number) => String(id))
    : [];
};

const appendShownIds = (
  platform: "edit" | "view" | "api",
  key: "shownAnnouncementStepIds" | "shownAnnouncementHintIds",
  newIds: string[]
) => {
  const storageKey = `${platform}${INTRO_STORAGE_SUFFIX}`;
  const all = getIntroShownStorage(platform);
  const existingIds = Array.isArray(all[key])
    ? all[key].map((id: string | number) => String(id))
    : [];

  all[key] = Array.from(new Set([...existingIds, ...newIds]));
  localStorage.setItem(storageKey, JSON.stringify(all));
};

const getShownAnnouncementStepIds = (platform: "edit" | "view" | "api") =>
  getShownIds(platform, "shownAnnouncementStepIds");

const setShownAnnouncementStepIds = (
  platform: "edit" | "view" | "api",
  steps: AnnouncementStep[]
) =>
  appendShownIds(
    platform,
    "shownAnnouncementStepIds",
    steps.map((step) => String(step._id))
  );

const getShownAnnouncementHintIds = (platform: "edit" | "view" | "api") =>
  getShownIds(platform, "shownAnnouncementHintIds");

const setShownAnnouncementHintIds = (
  platform: "edit" | "view" | "api",
  hintIds: string[]
) => appendShownIds(platform, "shownAnnouncementHintIds", hintIds);

const shouldShowInCurrentStage = (item: Announcement) => {
  const configuredStages = item.showInStage
    ?.map((stage) => stage.trim().toLowerCase())
    .filter(Boolean);

  if (!configuredStages || configuredStages.length === 0) {
    return true;
  }

  return configuredStages.includes(ClientConfig.stage.trim().toLowerCase());
};

const fetchCmsItems = async <T,>(
  endpoint: string,
  locale: string,
  query: Record<string, string> = {}
): Promise<T[]> => {
  if (!CMS_API_KEY) {
    throw new Error("Missing ANNOUNCEMENT_API_KEY configuration");
  }

  const searchParams = new URLSearchParams({ locale, ...query });
  const response = await fetch(
    `${CMS_BASE_URL}/api/content/items/${endpoint}?${searchParams.toString()}`,
    {
      method: "GET",
      headers: {
        "Api-Key": CMS_API_KEY,
      },
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP error: ${response.status} ${response.statusText}`);
  }

  const data: unknown = await response.json();
  if (!Array.isArray(data)) {
    throw new Error(`Invalid ${endpoint} response format`);
  }

  return data as T[];
};

export default function IntroSystem({
  role,
}: {
  role: "edit" | "view" | "api";
}) {
  const introductionStep = defaultStore((state) => state.introductionStep);
  const changeIntroductionStep = defaultStore(
    (state) => state.changeIntroductionStep
  );
  const showIntroduction = defaultStore((state) => state.showIntroduction);
  const changeShowIntroduction = defaultStore(
    (state) => state.changeShowIntroduction
  );
  const showAnnouncements = defaultStore((state) => state.showWhatsNew);
  const changeShowAnnouncements = defaultStore(
    (state) => state.changeShowWhatsNew
  );
  const showAxisscale = defaultStore((state) => state.showAxisscale);
  const changeMapExpand = defaultStore((state) => state.changeMapExpand);
  const showStreetMap = defaultStore((state) => state.showStreetMap);

  const changeTopicPopover = sidebarStore((state) => state.changeTopicPopover);

  const changeShowHelp = topbarStore((state) => state.changeShowHelp);
  const changeAnchorHelp = topbarStore((state) => state.changeAnchorHelp);
  const changeAnchorUser = topbarStore((state) => state.changeAnchorUser);

  const { showMessage } = useSnackbar();

  const introAnnouncementsHintsRef = useRef(introJs());
  const introAnnouncementsHints = introAnnouncementsHintsRef.current;

  const [announcementSteps, setAnnouncementSteps] = useState<
    AnnouncementStep[]
  >([]);
  const [announcementHints, setAnnouncementHints] = useState<
    AnnouncementHint[]
  >([]);
  const announcementStepsRef = useRef<AnnouncementStep[]>([]);
  const filteredAnnouncementHintsRef = useRef<AnnouncementHint[]>([]);

  const { introductionSteps } =
    role === "edit"
      ? EditSteps(showAxisscale || false)
      : ViewSteps(showAxisscale || false);

  const linrefIntroductionSteps = role === "edit" ? [10] : [8];

  const { t, i18n } = useTranslation();

  const filteredIntroductionSteps = useMemo(() => {
    // filter out the linrefsteps when no linref enabled
    if (!showAxisscale) {
      return introductionSteps.filter(
        (_, index) => !linrefIntroductionSteps.includes(index)
      );
    }
    return introductionSteps;
  }, [introductionSteps, showAxisscale]);

  const linrefAnnouncementHints = role === "edit" ? [10] : [];

  const filteredAnnouncementHints = useMemo(() => {
    // filter out the linrefhints when no linref enabled
    if (!showAxisscale) {
      return announcementHints.filter(
        (_, index) => !linrefAnnouncementHints.includes(index)
      );
    }
    return announcementHints;
  }, [announcementHints, showAxisscale]);

  const filteredAnnouncementHintsForIntro = useMemo(
    () =>
      filteredAnnouncementHints.map((hint): Hint => ({
        element: hint.anchorElement,
        hint: hint.contentText,
      })),
    [filteredAnnouncementHints]
  );

  const shownAnnouncementStepIds = new Set(getShownAnnouncementStepIds(role));
  const shownAnnouncementHintIds = new Set(getShownAnnouncementHintIds(role));
  const unseenAnnouncementSteps = announcementSteps.filter(
    (step) => !shownAnnouncementStepIds.has(String(step._id))
  );
  const hasUnseenAnnouncementHints = filteredAnnouncementHints.some(
    (hint) => !shownAnnouncementHintIds.has(String(hint._id))
  );

  useEffect(() => {
    announcementStepsRef.current = announcementSteps;
  }, [announcementSteps]);

  useEffect(() => {
    filteredAnnouncementHintsRef.current = filteredAnnouncementHints;
  }, [filteredAnnouncementHints]);

  const getUnseenAnnouncementSteps = () => {
    const shownStepIds = new Set(getShownAnnouncementStepIds(role));
    return announcementStepsRef.current.filter(
      (step) => !shownStepIds.has(String(step._id))
    );
  };

  const hasUnseenAnnouncementHintsByIds = () => {
    const shownHintIds = new Set(getShownAnnouncementHintIds(role));
    return filteredAnnouncementHintsRef.current.some(
      (hint) => !shownHintIds.has(String(hint._id))
    );
  };

  const shouldIntroductionStepsBeShown = () =>
    !getIntroductionShownLS(role, "introduction");

  const showComponent = (title: string) => {
    const nearbySteps = [
      filteredIntroductionSteps[introductionStep - 1],
      filteredIntroductionSteps[introductionStep],
      filteredIntroductionSteps[introductionStep + 1],
    ];

    return nearbySteps.some((step) => step?.title === title);
  };

  useEffect(() => {
    // Map
    if (showComponent(t("onboarding_edit.title_5"))) {
      changeMapExpand(true);
    } else {
      changeMapExpand(false);
    }
    // Ebenen
    if (showComponent(t("onboarding_edit.title_7"))) {
      changeTopicPopover(true);
    }
    // Help menu
    if (showComponent(t("onboarding_edit.title_18"))) {
      changeTopicPopover(false);
      const el: HTMLElement | null =
        document.getElementById("infra3d-helpButton");
      changeAnchorHelp(el);
      changeShowHelp(true);
    } else {
      changeAnchorHelp(null);
      changeShowHelp(false);
    }
    // User menu
    if (showComponent(t("onboarding_edit.title_20"))) {
      const el: HTMLElement | null = document.getElementById("avatarMenu");
      changeAnchorUser(el);
    } else {
      changeAnchorUser(null);
    }
  }, [introductionStep]);

  const defaultOptionsSteps = {
    nextToDone: true,
    exitOnEsc: true,
    tooltipClass: "infra3d-introductionBox",
    showProgress: true,
    showBullets: false,
    disableInteraction: true,
    nextLabel: t("onboarding_buttons.next"),
    prevLabel: t("onboarding_buttons.prev"),
    doneLabel: t("onboarding_buttons.done"),
  };

  useEffect(() => {
    // remove the old "whatsnew" entry from the introShown storage objects,
    // keeping the rest of the object (e.g. "introduction") intact
    // can be removed after the next release
    const storageKey = `${role}${INTRO_STORAGE_SUFFIX}`;
    const raw = localStorage.getItem(storageKey);
    if (!raw) return;

    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && "whatsnew" in parsed) {
        delete parsed.whatsnew;
        localStorage.setItem(storageKey, JSON.stringify(parsed));
      }
    } catch {
      // Ignore malformed legacy values.
    }
  }, [role]);

  useEffect(() => {
    if (showStreetMap) {
      introAnnouncementsHints.removeHints();
      return;
    }
    const cancelWait = waitForAllElements({
      selectors: filteredIntroductionSteps.map((step) => step.element),
      onReady: () => {
        if (shouldIntroductionStepsBeShown()) {
          startIntroductionSteps();
        } else {
          const currentUnseenAnnouncementSteps = getUnseenAnnouncementSteps();
          if (currentUnseenAnnouncementSteps.length > 0) {
            startAnnouncementSteps(currentUnseenAnnouncementSteps);
          }
          if (hasUnseenAnnouncementHintsByIds()) {
            startAnnouncementHints(false);
          }
        }
      },
    });

    return () => {
      cancelWait();
    };
  }, [
    filteredIntroductionSteps,
    hasUnseenAnnouncementHints,
    showStreetMap,
    unseenAnnouncementSteps.length,
  ]);

  useEffect(() => {
    // start via help menu
    if (showIntroduction) {
      startIntroductionSteps();
    }
    if (showAnnouncements) {
      if (
        announcementSteps.length === 0 &&
        filteredAnnouncementHintsForIntro.length === 0
      ) {
        showMessage(t("general.no_news"), "info");
        changeShowAnnouncements(false);
      }
      if (announcementSteps.length > 0) {
        startAnnouncementSteps();
      }
      if (filteredAnnouncementHintsForIntro.length > 0) {
        startAnnouncementHints(true);
      }
    }
  }, [
    announcementSteps.length,
    filteredAnnouncementHintsForIntro.length,
    showIntroduction,
    showAnnouncements,
  ]);

  useEffect(() => {
    let cancelled = false;
    const announcementStepsEndpoint =
      role === "view" ? "AnnouncementStepsView" : "AnnouncementStepsEdit";

    fetchCmsItems<AnnouncementStep>(announcementStepsEndpoint, i18n.language, {
      "sort[_created]": "-1", // always show newest first
    })
      .then((announcements) => {
        if (cancelled) return;
        setAnnouncementSteps(announcements.filter(shouldShowInCurrentStage));
      })
      .catch((error: Error) => {
        if (cancelled) return;
        console.error(error);
        showMessage(error.message, "error");
      });

    return () => {
      cancelled = true;
    };
  }, [i18n.language, role, showMessage]);

  useEffect(() => {
    let cancelled = false;
    const announcementHintsEndpoint =
      role === "view" ? "AnnouncementHintsView" : "AnnouncementHintsEdit";

    fetchCmsItems<AnnouncementHint>(announcementHintsEndpoint, i18n.language)
      .then((hints) => {
        if (cancelled) return;
        setAnnouncementHints(hints.filter(shouldShowInCurrentStage));
      })
      .catch((error: Error) => {
        if (cancelled) return;
        console.error(error);
        showMessage(error.message, "error");
      });

    return () => {
      cancelled = true;
    };
  }, [i18n.language, role, showMessage]);

  const introRunning = useRef(false);

  const startIntroductionSteps = () => {
    if (introRunning.current) return; // Don't start if already running

    introRunning.current = true;

    const intro = introJs();

    intro.setOptions({
      ...defaultOptionsSteps,
      steps: filteredIntroductionSteps,
    });

    intro.onchange(() => {
      const currentStep = intro.currentStep();

      if (currentStep) {
        changeIntroductionStep(currentStep);
      }
    });

    intro.onexit(() => {
      introRunning.current = false;
      setIntroductionShownLS(role, true);
      changeShowIntroduction(false);
      changeIntroductionStep(0);

      const currentUnseenAnnouncementSteps = getUnseenAnnouncementSteps();
      if (currentUnseenAnnouncementSteps.length > 0) {
        startAnnouncementSteps(currentUnseenAnnouncementSteps);
      }
      if (hasUnseenAnnouncementHintsByIds()) {
        startAnnouncementHints(false);
      }
    });

    intro.start();
  };

  const startAnnouncementSteps = (
    stepsToShow: AnnouncementStep[] = announcementSteps
  ) => {
    const intro = introJs();
    const mappedSteps = stepsToShow.map((step) => ({
      element: step.anchorElement === "" ? null : step.anchorElement,
      title: step.title,
      intro: `${step.contentText}${renderMediaMarkup(step)}`,
    }));

    intro.setOptions({
      ...defaultOptionsSteps,
      tooltipClass: "infra3d-announcementsBox",
      showProgress: false,
      steps: mappedSteps,
    });

    intro.onexit(() => {
      setShownAnnouncementStepIds(role, stepsToShow);
      changeShowAnnouncements(false);
    });

    intro.start();
  };

  const startAnnouncementHints = (reset: boolean) => {
    // Clear existing hints container (force re-init)
    const existingHints = document.querySelectorAll(".introjs-hint");
    existingHints.forEach((el) => el.remove());

    introAnnouncementsHints.setOptions({
      hints: filteredAnnouncementHintsForIntro,
      hintButtonLabel: t("onboarding_buttons.got_it") as string,
    });

    introAnnouncementsHints.onhintclose((hintIndex) => {
      const hintId = filteredAnnouncementHints[hintIndex]?._id;
      if (!hintId) {
        console.warn("Missing announcement hint id for index", hintIndex);
        return;
      }
      setShownAnnouncementHintIds(role, [String(hintId)]);
    });

    introAnnouncementsHints.addHints();

    window.removeEventListener("resize", handleResize);
    window.addEventListener("resize", handleResize);

    if (!reset) {
      hideAlreadyShownHints();
    }
  };

  const hideAlreadyShownHints = useCallback(() => {
    const shownHintIds = getShownAnnouncementHintIds(role);
    if (shownHintIds.length > 0) {
      filteredAnnouncementHints.forEach((hint, index) => {
        if (!shownHintIds.includes(String(hint._id))) return;
        introAnnouncementsHints.removeHint(index);
      });
    }
  }, [filteredAnnouncementHints, introAnnouncementsHints, role]);

  const handleResize = useMemo(
    () =>
      debounce(() => {
        introAnnouncementsHints.removeHints(); // Remove old positioned hints
        introAnnouncementsHints.addHints(); // Recalculate positions and add again
        hideAlreadyShownHints(); // Re-hide any already seen hints
      }, 300),
    [hideAlreadyShownHints, introAnnouncementsHints]
  );

  useEffect(() => {
    return () => {
      window.removeEventListener("resize", handleResize);
      handleResize.cancel();
      introAnnouncementsHints.removeHints();
    };
  }, [handleResize, introAnnouncementsHints]);

  return null;
}
